import { use, useEffect, useEffectEvent, useRef, useState } from 'react';

import { logger } from '../../../core/Log/logger';
import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { useDeepCompareMemoize } from '../../../shared/hooks/useDeepCompareMemoize';

import { useWemap } from './hooks/useWemap';
import {
    MapClickEvent,
    MapMovedEvent,
    PinpointClickEvent,
    PinpointEvent,
    WemapBounds,
    WemapBoundsPadding,
    WemapLivemap,
    WemapPinpoint,
    WemapSDK,
} from './types/Wemap';
import { WemapDrawerContent } from './types/WemapDrawerContent';
import { loadWemapSDK } from './utilities/loadWemapSdk';
import { WemapDrawer } from './WemapDrawer';
import style from './WemapMap.module.css';

const DEFAULT_POLYLINE_OPTIONS = {
    color: '#01543C',
    width: 3,
    opacity: 1,
    useNetwork: false,
};

const isIframeEmbed = false;

/**
 * We store the createLivemap function because it is no longer available in the WemapSDK
 * once a map is created... Waiting for an answer from Wemap if it is intentional or not
 * and how to handle it properly.
 */
let createLivemap: WemapSDK['v1']['createLivemap'];

export type WemapMapProps = {
    /**
     * To enable the deep linking on the map.
     * Deep linking is when the map state is synchronized with the URL.
     * When deep linking is enabled, the map will be initialized with the state from the URL.
     * See: https://developers.getwemap.com/docs/web/embed-sdk/deeplinking
     */
    deepLinkingEnabled?: boolean;

    /**
     * To control the initial route on the map when deep linking is enabled.
     * See: https://developers.getwemap.com/docs/web/embed-sdk/deeplinking
     */
    initialRoute?: {
        name: string;
        coordinates?: { center: { lat: number; lng: number }; zoom: number };
        query?: { query?: string; tags?: string };
    };

    /**
     * To disable the clustering on the map (default is true).
     */
    clustering?: boolean;

    /**
     * Pinpoints list to display on the map.
     * See: https://developers.getwemap.com/docs/web/embed-sdk/livemap#pinpoint
     */
    pinpoints: WemapPinpoint[];

    /**
     * To fit the map boundaries.
     * See: https://developers.getwemap.com/docs/web/embed-sdk/livemap#fitbounds
     */
    bounds?: WemapBounds;

    /**
     * To set the padding added to the bounds.
     * See: https://developers.getwemap.com/docs/web/embed-sdk/livemap#fitbounds
     */
    boundsPadding: Required<WemapBoundsPadding>;

    /**
     * To fit the map to the boundaries or the pinpoints or not.
     */
    fitPinpointsBounds?: boolean;

    /**
     * Array of pinpoint IDs to connect with polylines in the specified order.
     * Pinpoint IDs can appear multiple times to create complex routes.
     * Example: [1, 4, 928, 29, 13, 928, 1] will draw lines 1→4→928→29→13→928→1.
     * Used to connect points in sequence (step 1, step 2, step 3, etc.).
     */
    connectedPinpointIds?: number[];

    /**
     * Polyline style options.
     */
    polylineOptions?: {
        color?: string;
        width?: number;
    };

    /**
     * To control if the drawer is closable or not (close drawer and opened pinpoint).
     */
    isDrawerClosable?: boolean;

    /**
     * To control if the drawer is hideable or not (visually hide the drawer, but don't close it).
     */
    isDrawerHideable?: boolean;

    /**
     * To control if the drawer is open or not.
     */
    isDrawerOpen: boolean;

    /**
     * Callback to render drawer content when a pinpoint is opened.
     */
    renderDrawer?: (params: { openedPinpointId: number | null }) => WemapDrawerContent;

    /**
     * To handle the map tracking.
     */
    trackingInformation: {
        placeClicked: TrackingProperties<typeof ampli.poiClicked>;
    };

    onReady?: (livemap: WemapLivemap) => void;
    onLoadError?: (error: Error) => void;

    onMapClick?: (event: MapClickEvent) => void;
    onMapLongClick?: (event: MapClickEvent) => void;

    onMapMoved?: (event: MapMovedEvent) => void;
    onMapUpdate?: () => void;

    onPinpointClick?: (event: PinpointClickEvent) => void;
    onPinpointOpen?: (event: PinpointEvent) => void;
    onPinpointClose?: () => void;
};

export function WemapMap(props: WemapMapProps) {
    const {
        deepLinkingEnabled = false,
        clustering = true,
        initialRoute,
        fitPinpointsBounds = false,
        boundsPadding,
        isDrawerOpen,
        onReady,
        onLoadError,
        onMapClick,
        onMapLongClick,
        onMapMoved,
        onMapUpdate,
        onPinpointClick,
        onPinpointOpen,
        onPinpointClose,
    } = props;

    const { trackEvent } = useTrackEventAmplitude();
    const { emmid, openedPinpointId, setOpenedPinpointId, isDrawerExpanded } = useWemap();
    const sdk = use(loadWemapSDK());

    const mapContainerRef = useRef<HTMLDivElement>(null);
    const livemapRef = useRef<WemapLivemap | null>(null);

    const [isLivemapReady, setIsLivemapReady] = useState(false);

    const bounds = useDeepCompareMemoize(props.bounds);
    const currentBoundsRef = useRef<WemapBounds | null>(null);
    const currentPinpointsRef = useRef<WemapPinpoint[] | null>(null);
    const pinpoints = useDeepCompareMemoize(props.pinpoints);
    const trackingInformation = useDeepCompareMemoize(props.trackingInformation);
    const connectedPinpointIds = useDeepCompareMemoize(props.connectedPinpointIds);
    const polylineOptions = useDeepCompareMemoize(
        props.polylineOptions ?? DEFAULT_POLYLINE_OPTIONS,
    );

    const eventHandlersRef = useRef({
        onReady,
        onLoadError,
        onMapClick,
        onMapLongClick,
        onMapMoved,
        onMapUpdate,
        onPinpointClick,
        onPinpointOpen,
        onPinpointClose,
    });

    eventHandlersRef.current = {
        onReady,
        onLoadError,
        onMapClick,
        onMapLongClick,
        onMapMoved,
        onMapUpdate,
        onPinpointClick,
        onPinpointOpen,
        onPinpointClose,
    };

    const onGetBoundsPadding = useEffectEvent(function () {
        const { top, right, bottom, left } = boundsPadding;
        return {
            top,
            right,
            bottom,
            left: left + (isDrawerOpen || isDrawerExpanded ? WemapDrawer.WIDTH : 0),
        };
    });

    const onInitializeWemap = useEffectEvent(function (container: HTMLElement) {
        createLivemap = createLivemap ?? sdk.v1.createLivemap.bind(sdk.v1);

        const livemap = createLivemap(
            container,
            {
                emmid,
                token: 'VAJ7N848U1V62Q4M1JLFWHDBO', // gitleaks:allow
                deeplinkingenabled: deepLinkingEnabled,
                clustering,
                ...(initialRoute && { initialRoute }),
            },
            isIframeEmbed,
        );

        livemapRef.current = livemap;

        const mapClickHandler = (event: MapClickEvent) => {
            eventHandlersRef.current.onMapClick?.(event);
        };
        livemap.addEventListener('mapClick', mapClickHandler);

        const mapLongClickHandler = (event: MapClickEvent) => {
            eventHandlersRef.current.onMapLongClick?.(event);
        };
        livemap.addEventListener('mapLongClick', mapLongClickHandler);

        const mapMovedHandler = (event: MapMovedEvent) => {
            eventHandlersRef.current.onMapMoved?.(event);
        };
        livemap.addEventListener('livemapMoved', mapMovedHandler);

        const mapUpdateHandler = () => {
            eventHandlersRef.current.onMapUpdate?.();
        };
        livemap.addEventListener('mapUpdate', mapUpdateHandler);

        const pinpointClickHandler = (event: PinpointClickEvent) => {
            setOpenedPinpointId(event.pinpoint.id);
            trackEvent((ampli, defaultProperties) =>
                ampli.poiClicked({
                    ...defaultProperties,
                    ...trackingInformation.placeClicked,
                }),
            );
            eventHandlersRef.current.onPinpointClick?.(event);
        };
        livemap.addEventListener('pinpointClick', pinpointClickHandler);

        const pinpointOpenHandler = (event: PinpointEvent) => {
            eventHandlersRef.current.onPinpointOpen?.(event);
        };
        livemap.addEventListener('pinpointOpen', pinpointOpenHandler);

        const pinpointCloseHandler = () => {
            eventHandlersRef.current.onPinpointClose?.();
        };
        livemap.addEventListener('pinpointClose', pinpointCloseHandler);

        livemap.onLoadError((error) => {
            logger.error('Wemap SDK error', { ...props, error });
            eventHandlersRef.current.onLoadError?.(error);
        });

        void livemap.waitForReady().then(() => {
            livemap.disableAnalytics();
            setIsLivemapReady(true);
            eventHandlersRef.current.onReady?.(livemap);
        });
    });

    useEffect(() => {
        if (mapContainerRef.current && !livemapRef.current) {
            onInitializeWemap(mapContainerRef.current);
        }
    });

    useEffect(() => {
        if (!isLivemapReady || !livemapRef.current) {
            return;
        }

        const livemap = livemapRef.current;

        async function updateBoundsAndPinpoints() {
            try {
                if (bounds && bounds !== currentBoundsRef.current) {
                    currentBoundsRef.current = bounds;
                    await livemap.fitBounds(bounds, {
                        animate: false,
                        padding: onGetBoundsPadding(),
                    });
                }
            } catch (error) {
                logger.error('Wemap error while fitting bounds', { bounds, error });
            }

            try {
                if (pinpoints !== currentPinpointsRef.current) {
                    currentPinpointsRef.current = pinpoints;
                    const pinpointsResult = await livemap.setPinpoints(pinpoints);

                    if (fitPinpointsBounds && pinpointsResult.bounds) {
                        await livemap.fitBounds(pinpointsResult.bounds.toArray(), {
                            animate: true,
                            padding: onGetBoundsPadding(),
                        });
                    }
                }
            } catch (error) {
                logger.error('Wemap error while setting pinpoints', { error });
            }

            const openedPinpointData = pinpoints?.find((pinpoint) => {
                return pinpoint.id === openedPinpointId;
            });

            if (!openedPinpointId || !openedPinpointData) {
                return;
            }

            try {
                await livemap.openPinpoint(openedPinpointId);
            } catch (error) {
                logger.error('Wemap error while opening pinpoint', {
                    pinpointId: openedPinpointId,
                    error,
                });
            }

            try {
                await new Promise((resolve) => setTimeout(resolve, 500));
                await livemap.easeTo({
                    center: {
                        longitude: openedPinpointData.longitude,
                        latitude: openedPinpointData.latitude,
                    },
                    padding: onGetBoundsPadding(),
                });
            } catch (error) {
                logger.error('Wemap error while easing to opened pinpoint', {
                    pointOfInterest: openedPinpointData,
                    error,
                });
            }
        }

        void updateBoundsAndPinpoints();
    }, [isLivemapReady, livemapRef, pinpoints, fitPinpointsBounds, bounds, openedPinpointId]);

    useEffect(() => {
        let polylineId: string | undefined;

        if (!isLivemapReady || !livemapRef.current) {
            return;
        }

        if (!connectedPinpointIds?.length || !pinpoints?.length) {
            return;
        }

        const coordinates = connectedPinpointIds
            .map((pinpointId) => {
                const pinpoint = pinpoints.find((p) => {
                    return p.id === pinpointId;
                });

                if (!pinpoint) {
                    return null;
                }
                return {
                    latitude: pinpoint.latitude,
                    longitude: pinpoint.longitude,
                };
            })
            .filter((coord) => {
                return coord !== null;
            });

        if (coordinates.length < 2) {
            return;
        }

        void livemapRef.current
            .drawPolyline(coordinates, polylineOptions)
            .then(({ id }) => {
                polylineId = id;
            })
            .catch((error) => {
                logger.error('Wemap error while drawing polyline', { coordinates, error });
            });

        return () => {
            if (!polylineId) {
                return;
            }
            void livemapRef.current?.removePolyline(polylineId);
        };
    }, [isLivemapReady, livemapRef, connectedPinpointIds, pinpoints, polylineOptions]);

    return <div ref={mapContainerRef} className={style.map} />;
}
